/**
 * Regelversion 12: Neue Werkzeuge kommen in der ersten Legislatur nach und nach dazu.
 *
 * Reine Oberfläche: Die Systeme wirken von Anfang an (Verbände, Schere usw.), nur Anzeigen und Aktionen
 * erscheinen später. Wer die Tipps abgeschaltet hat oder die Einführung schon einmal durchlaufen hat,
 * bekommt alles sofort. Wirtschaftsordnung und Machtfragen sind ab Version 12 auch eine Spielregel:
 * Sie gibt es erst ab der zweiten Legislatur (engine/order11ui.ts, engine/dem11.ts).
 */
import { useS } from '../../store/game';
import { useTutorial } from './store';
import { since } from '../../engine/version';
import { coalSeats } from '../../engine/election';
import { NEED } from '../../content/consts';
import { LV_MIN, lvlOf } from '../../engine/level12';

export type Unlock = { id: string; m: number; t: string; d: string; tab?: string };
/** Ab welchem Monat der ersten Legislatur (0 = erster Monat). */
export const UNLOCKS: Unlock[] = [
  { id: 'presse', m: 3, t: 'Kommunikation', tab: 'presse', d: 'Jeden Monat habt ihr einen Termin für Öffentlichkeitsarbeit: ein Thema setzen, ein Gesetz erklären oder öffentlich auftreten. Termine sammeln sich bis zu drei an. Ihr findet sie im Reiter „Presse“.' },
  { id: 'agenda', m: 7, t: 'Vorhaben aus den Meinungsthemen', tab: 'lage', d: 'Neben den Problemen streitet das Land über Grundsatzfragen wie Mindestlohn, Tempolimit oder Wehrpflicht. Unter „Lage & Gesetze“ könnt ihr dazu Gesetze anstoßen. Passen sie zur Mehrheitsmeinung, bringt das Zustimmung. Im Kabinett gibt es jetzt auch den Koalitionsausschuss.' },
  { id: 'stimmung', m: 11, t: 'Verbände, Parteiflügel und Zahlen', tab: 'stimmung', d: 'Eure Gesetze haben Folgen: Gewerkschaften, Arbeitgeber und Sozialverbände loben oder protestieren, die Flügel eurer Partei murren, die soziale Schere bewegt sich. Im Reiter „Stimmung“ und im neuen Reiter „Zahlen“ seht ihr das.' },
  { id: 'verwaltung', m: 17, t: 'Verwaltung', tab: 'kabinett', d: 'Gesetze müssen auch umgesetzt werden. Im Reiter „Kabinett“ stattet ihr jetzt Ministerien mit Personal und Digitalisierung aus. Gut ausgestattete Ressorts setzen Gesetze schneller und wirksamer um.' },
];
/** Ab der zweiten Legislatur (in Version 12 auch als Spielregel). */
export const LATER: Unlock[] = [
  { id: 'ordnung', m: 0, t: 'Wirtschaftsordnung', tab: 'lage', d: 'Ihr kennt jetzt die Folgen eurer ersten Legislatur. Ab sofort könnt ihr das Land grundlegend umbauen: Richtung Staat oder Richtung Markt, mit Systemgesetzen von der Vergesellschaftung bis zur Flat Tax. Beides hat seinen Preis.' },
  { id: 'macht', m: 0, t: 'Machtfragen', tab: 'lage', d: 'Mit einer eigenen Mehrheit könntet ihr in Gerichte, Rundfunk und Wahlrecht eingreifen. Das Spiel zeigt, wie Demokratien ausgehöhlt werden und was sich dagegen stemmt. Wer so regiert, bekommt Widerstand und keine Orden.' },
];

/** Text einer Freischaltung. Ohne Verbände (Stufe 1 und 2) gibt es eine eigene Fassung für „Stimmung“. */
export function unlockText(S: any, u: Unlock): { t: string; d: string } {
  if (u.id === 'stimmung' && S?.cfg?.lobby === false) return { t: 'Stimmung und Zahlen', d: 'Eure Gesetze haben Folgen. Im Reiter „Stimmung“ seht ihr, wie die Wählergruppen auf eure Regierung blicken, im neuen Reiter „Zahlen“, wie sich Wirtschaft, Haushalt und soziale Schere entwickeln.' };
  return { t: u.t, d: u.d };
}
/** Wird in dieser Partie schrittweise eingeführt? */
export function progressiveOf(S: any, off: boolean, seen: string[]): boolean {
  return !!S && since(12) && !S.mp && !off && !seen.includes('unlock-done') && (S.leg ?? 0) === 0 && !(S.career?.length);
}
/** Ist ein Werkzeug sichtbar? */
export function isUnlocked(S: any, id: string, prog: boolean): boolean {
  if (since(12) && !S?.mp && LV_MIN[id] && lvlOf(S) < LV_MIN[id]) return false; // Erfahrungsstufe
  if (id === 'ordnung' || id === 'macht') {
    if (since(12) && !(S.career?.length)) return false; // Spielregel ab Version 12
    if (id === 'macht' && since(12) && coalSeats() < NEED) return false; // ohne Mehrheit nicht sichtbar
    return true;
  }
  if (id === 'haushalt') return !prog || !!S.budget || (S.budgets?.length ?? 0) > 0;
  const u = UNLOCKS.find((x) => x.id === id);
  if (!u || !prog) return true;
  return S.m - S.m0 >= u.m;
}
export function useUnlocked(): (id: string) => boolean {
  const S = useS(); const { off, seen } = useTutorial();
  const prog = progressiveOf(S, off, seen);
  return (id: string) => isUnlocked(S, id, prog);
}
/** Nächste noch nicht bestätigte Freischaltung (für die Karte). */
export function useNextUnlock(): Unlock | null {
  const S: any = useS(); const { off, seen } = useTutorial();
  if (!S || S.mp || !since(12) || off) return null;
  const prog = progressiveOf(S, off, seen);
  if (prog) { const u = UNLOCKS.find((x) => isUnlocked(S, x.id, prog) && !seen.includes('unlock-' + x.id)); if (u) return u; }
  // Nach einem Aufstieg: Werkzeuge der neuen Stufe
  const lv = UNLOCKS.find((x) => (LV_MIN[x.id] ?? 1) > 1 && (S.leg ?? 0) > 0 && isUnlocked(S, x.id, prog) && !seen.includes('unlock-' + x.id));
  if (lv) return lv;
  return LATER.find((x) => isUnlocked(S, x.id, prog) && !seen.includes('unlock-' + x.id)) || null;
}
